import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import imageutils from '@common/utils/imageutils';
import { getZeroPadNumber } from '@common/utils/stringUtils';
import ThronAudio from '@components/utils/ThronAudio';
import Lazyimg from '@components/utils/Lazyimg';
import '@stone/scss/components/component-audio/_component-audio.scss';
import Icon from '@stone/ui/icon/Icon';
import Button from '@stone/ui/button/Button';
import { useLangChecker } from '@common/utils/i18n';
const Track = (props) => {
    const { title, thronid, index } = props;
    const { t } = useTranslation();
    const player = useRef();
    const [selected, setSelected] = useState(props.selected);
    const [isPlaying, setIsPlaying] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const togglePlaying = useCallback(() => {
        var _a, _b;
        if (!selected) {
            setSelected(true);
        }
        else {
            isPlaying ? (_a = player.current) === null || _a === void 0 ? void 0 : _a.pause() : (_b = player.current) === null || _b === void 0 ? void 0 : _b.play();
        }
    }, [selected, isPlaying]);
    useEffect(() => {
        setSelected(props.selected);
        if (props.selected && player.current) {
            props.onTrackChange && props.onTrackChange(player.current, initialized);
        }
    }, [props.selected]);
    return (React.createElement("li", { className: classNames('row row--inner font__cta audiotrack', { interactive: props.interactive }), "aria-current": selected, "aria-label": `${t('label.sound')} ${index + 1} ${title}`, onClick: () => props.isButton && props.changeId && props.changeId(props.ID), onKeyPress: (e) => e.key === 'Enter' && props.isButton && props.changeId && props.changeId(props.ID), role: 'menuitem', tabIndex: props.isButton ? 0 : undefined },
        React.createElement("div", { className: "audiotrack__index", "aria-hidden": true },
            React.createElement("span", null,
                React.createElement("span", { ref: useLangChecker, className: "d-none d-lg-inline" }, t('label.sound')),
                ' ',
                getZeroPadNumber(index + 1, 3))),
        React.createElement("div", { className: "audiotrack__name" },
            React.createElement("span", { "aria-hidden": true, tabIndex: -1 }, title)),
        React.createElement("div", { className: "audiotrack__vawe" },
            props.interactive && (React.createElement(Button, { className: "audiotrack__vawe-play", cls: ['button--quaternary'], type: "button", onClick: togglePlaying }, (isPlaying && t('button.pauseshort')) || React.createElement("span", { ref: useLangChecker }, t('button.playshort')))),
            (selected || initialized) && (React.createElement("div", { className: classNames('audiotrack__vawe-thron', {
                    opacized: !initialized,
                    'd-none': !selected,
                }), "aria-hidden": "true", tabIndex: -1 },
                React.createElement(ThronAudio, { autoplay: props.autoplay || false, loop: false, loopTimes: 1, thronID: thronid, onPauseCallback: () => {
                        setIsPlaying(false);
                        props.onPauseCallback && props.onPauseCallback();
                    }, onPlayCallback: (isFirstPlay) => {
                        setIsPlaying(true);
                        props.onPlayCallback && props.onPlayCallback(isFirstPlay);
                    }, onCompleteCallback: (playerInstance) => {
                        setIsPlaying(false);
                        props.onCompleteCallback && props.onCompleteCallback(playerInstance);
                    }, onInitCallback: (playerInstance) => {
                        player.current = playerInstance;
                        setInitialized(true);
                        props.onInitCallback && props.onInitCallback(playerInstance);
                        props.onTrackChange && props.onTrackChange(playerInstance, initialized);
                    }, onContentReady: (playerInstance) => props.onContentReady && props.onContentReady(playerInstance), onTimeUpdate: props.onTimeUpdate, noskin: true }))))));
};
const ComponentAudio = (props) => {
    var _a, _b;
    const { t } = useTranslation();
    const Heading = props.heading;
    /* REFS */
    const componentWrapper = useRef();
    const timeElement = useRef();
    const currentPlayer = useRef();
    const firstPlayer = useRef();
    /* STATES */
    const [currentID, setCurrentID] = useState();
    const [autoplay, setAutoplay] = useState(false);
    const [audioStatus, setAudioStatus] = useState(null);
    /* MEMO */
    const tracksCount = useMemo(() => { var _a; return ((_a = props.tracks) === null || _a === void 0 ? void 0 : _a.length) || 0; }, [props.tracks]);
    const viewtype = useMemo(() => (tracksCount > 1 ? 'album' : 'track'), [tracksCount]);
    // const currentTrack = useMemo<ComponentAudioTrack>(() => props.tracks.find((t) => t.ID === currentID), [props.tracks, currentID]);
    const captions = useMemo(() => JSON.parse(props.captions || '[]'), [props.captions]);
    const isPlaying = useMemo(() => {
        return audioStatus === null || audioStatus === void 0 ? void 0 : audioStatus.playing;
    }, [audioStatus]);
    /* METHOD */
    const onComplete = useCallback((currentIndex, player) => {
        if (viewtype !== 'album') {
            window.devmode && console.log('TRACK COMPLETE - NO ALBUM, STOP PLAYNG');
            return;
        }
        const nextIndex = currentIndex + 1;
        if (nextIndex >= tracksCount) {
            window.devmode && console.log('TRACK COMPLETE - ALBUM - END');
            setAudioStatus({
                playing: false,
                duration: 0,
                currentTime: 0,
            });
            setAutoplay(false);
            setCurrentID(props.tracks[0].ID);
            return;
        }
        window.devmode && console.log('TRACK COMPLETE - ALBUM - NEXT TRACK:', props.tracks[nextIndex].ID, nextIndex);
        setCurrentID(props.tracks[nextIndex].ID);
        setAutoplay(true);
    }, [tracksCount]);
    /* CALLBACKS */
    const toggleCurrentPlaying = useCallback(() => {
        setTimeout(() => {
            var _a, _b;
            isPlaying ? (_a = currentPlayer.current) === null || _a === void 0 ? void 0 : _a.pause() : (_b = currentPlayer.current) === null || _b === void 0 ? void 0 : _b.play();
        }, 100);
    }, [isPlaying, currentPlayer]);
    const isInteractive = useCallback((selected) => {
        return !props.posterImage && selected;
    }, [props.posterImage]);
    /* USEEFFECTS */
    useLayoutEffect(() => {
        if ((audioStatus === null || audioStatus === void 0 ? void 0 : audioStatus.playing) && timeElement.current) {
            const time = (audioStatus === null || audioStatus === void 0 ? void 0 : audioStatus.currentTime) > 0 ? Math.floor(audioStatus === null || audioStatus === void 0 ? void 0 : audioStatus.currentTime) : Math.floor(audioStatus === null || audioStatus === void 0 ? void 0 : audioStatus.duration);
            const minutes = Math.floor(time / 60);
            const seconds = time - minutes * 60;
            const timestring = !time ? '00:00' : `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            timeElement.current.innerText = timestring;
        }
    }, [audioStatus, timeElement]);
    return (React.createElement("div", { ref: componentWrapper, className: "container component-audio" },
        React.createElement("div", { className: "row justify-center" },
            React.createElement("div", { className: "col col-lg-14 col-xxl-10" },
                React.createElement("div", { className: "component-audio__heading mb-40" },
                    React.createElement(Heading, { className: "font__title" }, props.title),
                    React.createElement("div", { className: "font__title mt-8" }, "\u2013")),
                props.copy && React.createElement("div", { className: "component-audio__copy", dangerouslySetInnerHTML: { __html: props.copy } }),
                props.posterImage && (React.createElement("div", { className: "component-audio__visual" },
                    React.createElement("div", { className: "component-audio__poster" },
                        !isPlaying && (React.createElement("div", { className: "poster__overlay adaptive" }, tracksCount > 0 && (React.createElement("button", { tabIndex: -1, "aria-hidden": "true", className: "poster__circle-button", onClick: () => {
                                var _a;
                                (_a = currentPlayer.current) === null || _a === void 0 ? void 0 : _a.play();
                            } },
                            React.createElement("span", { className: "font__title", ref: useLangChecker }, t('button.playshort')))))),
                        React.createElement(Lazyimg, { src: imageutils.transform((_a = props.posterImage) === null || _a === void 0 ? void 0 : _a.path, { width: 320, height: 320 }, ''), srcset: imageutils.srcset((_b = props.posterImage) === null || _b === void 0 ? void 0 : _b.path, (props.imageWidth && Math.min(props.imageWidth, 1256)) || 1256), dataSizes: "auto", ariaHidden: true, alt: '' })),
                    React.createElement("div", { className: "component-audio__statusbar row row--inner" },
                        React.createElement("div", { className: "component-audio__statusbar-play" },
                            React.createElement(Button, { type: "button", className: "button--playpause", onClick: toggleCurrentPlaying, cls: ['button--quaternary'] },
                                React.createElement(React.Fragment, null,
                                    React.createElement(Icon, { family: '16', name: isPlaying ? 'pause' : 'play' }),
                                    React.createElement("span", { className: "button--playpause_label" }, isPlaying ? t('button.pauseshort') : React.createElement("span", { ref: useLangChecker }, t('button.playshort')))))),
                        React.createElement("div", { className: "component-audio__statusbar-caption" },
                            (captions === null || captions === void 0 ? void 0 : captions[0]) && React.createElement("span", { className: "caption_a" }, captions[0]),
                            (captions === null || captions === void 0 ? void 0 : captions[1]) && React.createElement("span", { className: "caption_b" }, captions[1])),
                        React.createElement("div", { className: "component-audio__statusbar-timer", ref: timeElement }, "00:00")))),
                (tracksCount && (React.createElement("ol", { className: classNames('nolist component-audio__traklist', { 'd-none': tracksCount === 1 && props.posterImage }) }, props.tracks.map((track, _i) => (React.createElement(Track, Object.assign({ key: track.ID, selected: track.ID === currentID || (!currentID && _i === 0), autoplay: autoplay, index: _i, interactive: isInteractive(track.ID === currentID || (!currentID && _i === 0)) }, track, { onPauseCallback: () => {
                        setAudioStatus(Object.assign(Object.assign({}, audioStatus), { playing: false }));
                    }, 
                    // onPlayCallback={(isFirstPlay: any) => {}}
                    onCompleteCallback: (player) => {
                        onComplete(_i, player);
                    }, onInitCallback: (player) => {
                        currentPlayer.current = player;
                        currentID != track.ID && setCurrentID(track.ID);
                        if (_i === 0) {
                            firstPlayer.current = player;
                        }
                    }, onContentReady: (player) => {
                        isPlaying && player.play();
                    }, onTimeUpdate: (player) => {
                        setAudioStatus(player.status());
                    }, onTrackChange: (player, initialized) => {
                        currentPlayer.current = player;
                        player.seek(0);
                        setAudioStatus((prev) => (Object.assign(Object.assign({}, prev), { currentTime: 0 })));
                        isPlaying && player.play();
                    }, changeId: setCurrentID, isButton: viewtype === 'album' }))))))) || React.createElement(React.Fragment, null, window.devmode && React.createElement("p", { className: "alert" }, "DEV NOTE. Please add, at least, an audio track"))))));
};
ComponentAudio.defaultProps = {
    heading: 'h2',
};
export default ComponentAudio;
